/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import get from 'lodash/get';
import {
    Spinner,
    SecurityHelper,
    NeedToKnow,
    Heading,
    FlexRow,
    FlexCol,
    Text,
    Divider,
    ContentBlock,
} from 'sarsaparilla';
import { withCompatRouter, COMPAT_ROUTER_PROP_TYPES } from 'sarsaparilla/legacy';
import { authModalActions } from 'shared-ui/auth/redux';
import ErrorNotification from 'ui-permit/src/components/ErrorNotification';
import GroupDetails from 'ui-permit/src/components/altPermitHolderConfirmationPage/GroupDetails';
import * as aActions from 'ui-account/src/actions/profileActions';
import AlternateHolderSideDetail from '../components/AlternateHolderSideDetail';
import * as pActions from '../actions/permit';
import { scrollUp } from '../utils/permitUtils';

export class AlternatePermitHolderConfirmationPage extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            account: {
                cell_phone: '',
                email: '',
                first_name: '',
                home_address: {
                    address1: '',
                    address2: '',
                    city: '',
                    state: '',
                    zip_code: '',
                },
                last_name: '',
                user_id: '',
            },
            errors: [],
            needToKnowChecked: false,
            saveLeader: false,
            validation: {},
        };
    }

    componentDidMount() {
        const loggedin = SecurityHelper.isLoggedIn();
        if (!loggedin) {
            this.props.openLoginModal();
        } else {
            this.fetchData();
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.account && !prevProps.account) {
            this.fetchData();
        }
    }

    fetchData() {
        const { permitId, reservationId } = this.props.router.params;
        this.props.pActions.verifyAlternate(permitId, reservationId).then(() => {
            this.props.pActions.fetchPermitReservationAlert(reservationId).then(() => {
                if (this.props.verifyAlternateError) {
                    pActions.markAlertAsRead(this.props.alertId);
                }
            });
        });

        this.props.pActions.fetchPermit(permitId).then(() => {
            this.props.pActions.fetchIssuance(reservationId, permitId);
            if (this.props.account) {
                this.setState({ account: this.props.account.account });
            }
        });
    }

    acceptAlternateRequest = () => {
        if (!this.isThisFormValid()) {
            scrollUp();
            return;
        }
        if (this.state.saveLeader) {
            this.saveLeaderInfo();
        }
        this.props.pActions
            .confirmAlternate(
                this.props.issuance.permit_id,
                this.props.issuance.id,
                true,
                this.state.account,
                this.props.alertId
            )
            .then(() => {
                if (!this.props.confirmAlternateError) {
                    window.location = '/account/reservations';
                }
            });
    };

    declineAlternateRequest = () => {
        this.props.pActions
            .confirmAlternate(
                this.props.issuance.permit_id,
                this.props.issuance.id,
                false,
                this.state.account,
                this.props.alertId
            )
            .then(() => {
                if (!this.props.declineAlternateError) {
                    window.location = '/account/reservations';
                }
            });
    };

    handleGroupDetailChange = (groupDetail) => {
        this.setState({ account: groupDetail.groupLeader });
    };

    handleSaveLeaderChange = (saveLeader) => {
        this.setState({ saveLeader });
    };

    handleNeedToKnowCheckboxChange = () => {
        this.setState(
            (prevState) => ({ needToKnowChecked: !prevState.needToKnowChecked }),
            () => {
                if (this.state.needToKnowChecked) {
                    this.isThisFormValid();
                    scrollUp();
                }
            }
        );
    };

    fetchPermitHolder() {
        /* eslint-disable camelcase */
        // returns an object with keys 'permit_holder' and 'permit_holder_address'
        // for use when sending issuance object away
        const {
            first_name,
            last_name,
            email,
            user_id,
            cell_phone: phone,
            home_address: homeAddress,
        } = this.state.account;
        const { city, state: state_code, zip_code: postal_code } = homeAddress;

        const permitHolderInfo = {
            first_name,
            last_name,
            email,
            user_id,
            phone,
            home_address: {
                street: [homeAddress.address1, homeAddress.address2],
                city,
                state_code,
                postal_code,
            },
        };
        return permitHolderInfo;
    }

    permitHolderValidation = () => {
        const INVALID = {
            PERMIT_HOLDER_FIELDS_MISSING:
                'Please fill out all required Permit Holder fields.',
        };
        const ph = this.fetchPermitHolder();
        const { home_address: pha } = ph;
        if (!(ph.first_name && ph.last_name && ph.email && ph.phone)) return INVALID;
        if (!(pha.street[0] && pha.city && pha.state_code && pha.postal_code)) {
            return INVALID;
        }
        return {};
    };

    isThisFormValid = () => {
        const validation = {
            ...this.permitHolderValidation(),
            ...(this.state.needToKnowChecked
                ? {}
                : { NEED_CHECK: 'Need to Know must be acknowledged' }),
        };
        const errors = Object.values(validation);
        this.setState({ errors, validation });
        return errors.length === 0;
    };

    createUpdatedAccountObject(prevAcct) {
        // eslint-disable-next-line
        const { first_name, last_name, home_address, email, cell_phone } =
            this.state.account;
        const acctObj = {
            ...prevAcct,
            first_name,
            last_name,
            home_address,
            email,
            cell_phone,
        };
        return acctObj;
    }

    saveLeaderInfo() {
        const acct = this.createUpdatedAccountObject(SecurityHelper.getAccount());
        this.props.aActions.updateAccount(acct);
    }

    render() {
        if (!this.props.permit.id || !this.props.permit.rules) {
            return (
                <div className="permit-loading-wrapper">
                    <Spinner />
                </div>
            );
        }

        const {
            confirmAlternateError,
            declineAlternateError,
            verifyAlternateError,
            verifyAlternateSuccess,
        } = this.props;
        const permitHolder = get(this.props.issuance, 'primary_permit_holder', null);
        const requester = permitHolder
            ? `${permitHolder.first_name} ${permitHolder.last_name}`
            : 'someone';
        const groupDetail = {
            groupLeaderType: '',
            groupLeader: this.state.account,
            otherLeader: {
                fName: '',
                mName: '',
                lName: '',
                phone: '',
                email: '',
            },
            altLeaders: [{ email: '' }],
        };
        const displayErrors = [...this.state.errors];
        if (!displayErrors.length) {
            displayErrors.push(
                confirmAlternateError,
                declineAlternateError,
                verifyAlternateError
            );
        }
        return (
            <ContentBlock>
                <FlexRow className="my-4">
                    <FlexCol lg={8} className="px-sm-4">
                        <Heading headingLevel={1} appearance="h3" className="mb-3">
                            Alternate Permit Holder Confirmation
                        </Heading>
                        <ErrorNotification errorHeader="Error:" errors={displayErrors} />
                        {!verifyAlternateError && (
                            <>
                                <Heading
                                    headingLevel={2}
                                    appearance="h5"
                                    className="mb-4"
                                >
                                    You have been asked by <b>{requester}</b> to be an
                                    alternate permit holder.
                                </Heading>
                                <div>
                                    <h1 className="hide">My Account</h1>
                                    <GroupDetails
                                        conditionalComponents={{
                                            alternate_group_leader: false,
                                        }}
                                        groupDetail={groupDetail}
                                        handleGroupDetailChange={
                                            this.handleGroupDetailChange
                                        }
                                        handleSaveLeaderChange={
                                            this.handleSaveLeaderChange
                                        }
                                        validatingAlternateHolder
                                    />
                                </div>
                                <Divider />
                                <section>
                                    <Heading
                                        headingLevel={2}
                                        appearance="h5"
                                        className="mb-3"
                                    >
                                        Alternate Permit Holders
                                    </Heading>
                                    <Text size="xl">Why an Alternate permit holder?</Text>
                                    <p>
                                        The permit holder is required to be present on the
                                        entire trip for this permit to be valid. In the
                                        event that the permit holder is unavailable,
                                        alternate permit holders can be designated at the
                                        time of the reservation.
                                    </p>
                                    <Text size="xl">
                                        How to Add an Alternate Permit Holder
                                    </Text>
                                    <p>
                                        Enter an email address for an alternate permit
                                        holder. The alternate permit holder must respond
                                        and accept being an alternate permit within 72
                                        hours or this permit will not have an alternate
                                        permit holder. If the alternate permit holder does
                                        not have a recreation.gov account, they will be
                                        required to create an account.
                                    </p>
                                    <Text size="xl">
                                        Alternate Permit Holder Considerations
                                    </Text>
                                    <p>
                                        Being an alternate permit holder carries the same
                                        responsibilities as the primary permit holder. If
                                        there are seasonal reservations restrictions,
                                        these will apply to the alternate permit holder as
                                        if they are the primary permit holder. In other
                                        words, an alternate permit holder may not be able
                                        to reserve another permit or be an alternate
                                        permit holder for another permit at this facility
                                        for this season.
                                    </p>
                                </section>
                                <Divider />
                                <NeedToKnow
                                    siteName={this.props.permit.name}
                                    siteInfo={
                                        this.props.permit.description.need_to_know_order
                                    }
                                    isChecked={this.state.needToKnowChecked}
                                    onChange={this.handleNeedToKnowCheckboxChange}
                                />
                            </>
                        )}
                    </FlexCol>
                    {!verifyAlternateError && (
                        <FlexCol lg={4}>
                            <AlternateHolderSideDetail
                                permit={this.props.permit}
                                divisionName=""
                                entryDate={this.props.issuance.start_date}
                                exitDate={this.props.issuance.end_date}
                                buttonsDisabled={!verifyAlternateSuccess}
                                acceptAlternateRequest={this.acceptAlternateRequest}
                                declineAlternateRequest={this.declineAlternateRequest}
                            />
                        </FlexCol>
                    )}
                </FlexRow>
            </ContentBlock>
        );
    }
}

AlternatePermitHolderConfirmationPage.propTypes = {
    router: COMPAT_ROUTER_PROP_TYPES,
    pActions: PropTypes.object.isRequired,
    aActions: PropTypes.object.isRequired,
    openLoginModal: PropTypes.func.isRequired,
    permit: PropTypes.object.isRequired,
    issuance: PropTypes.object.isRequired,
    account: PropTypes.object,
    confirmAlternateError: PropTypes.string,
    alertId: PropTypes.string,
    declineAlternateError: PropTypes.string,
    verifyAlternateError: PropTypes.object,
    verifyAlternateSuccess: PropTypes.bool,
};

function mapDispatchToProps(dispatch) {
    return {
        pActions: bindActionCreators(pActions, dispatch),
        aActions: bindActionCreators(aActions, dispatch),
        openLoginModal: bindActionCreators(authModalActions.openLoginModal, dispatch),
    };
}

function select(state) {
    return {
        alertId: get(state.vehiclepermit, 'permit.reservation_alert.id'),
        permit: state.vehiclepermit.permit.result,
        issuance: state.vehiclepermit.reservation.issuance,
        fetchIssuanceError: state.vehiclepermit.reservation.fetch_err,
        validation: state.vehiclepermit.validation,
        verifyAlternateSuccess: state.vehiclepermit.permit.verify_alternate_success,
        verifyAlternateError: state.vehiclepermit.permit.verify_alternate_error,
        confirmAlternateSuccess: state.vehiclepermit.permit.confirm_alternate_success,
        confirmAlternateError: state.vehiclepermit.permit.confirm_alternate_error,
        account: state.auth.account,
    };
}

export default withCompatRouter(
    connect(select, mapDispatchToProps)(AlternatePermitHolderConfirmationPage)
);
